import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAuditLog } from "@/hooks/useAuditLog";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, Trash2, CalendarIcon, RotateCcw, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

type ResourceType = "chat" | "dashboard" | "query" | "workflow";

interface RecycleBinItem {
  id: string;
  name: string;
  resource_type: ResourceType;
  table_name: string;
  deleted_at: string;
  deleted_by_name: string | null;
  created_at: string;
}

const RESOURCE_LABELS: Record<ResourceType, string> = {
  chat: "Chat",
  dashboard: "Dashboard",
  query: "Query / Report",
  workflow: "Workflow",
};

const RESOURCE_COLORS: Record<ResourceType, string> = {
  chat: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  dashboard: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  query: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  workflow: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
};

const RecycleBin = () => {
  const { user, profile } = useAuth();
  const { log: auditLog } = useAuditLog();
  const { toast } = useToast();

  const [items, setItems] = useState<RecycleBinItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Filters
  const [filterType, setFilterType] = useState<string>("all");
  const [filterDateFrom, setFilterDateFrom] = useState<Date | undefined>(undefined);
  const [filterDateTo, setFilterDateTo] = useState<Date | undefined>(undefined);

  // Bulk action dialogs
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [singleDeleteTarget, setSingleDeleteTarget] = useState<RecycleBinItem | null>(null);

  const fetchItems = useCallback(async () => {
    if (!profile?.client_id) return;
    setLoading(true);

    const dateFromISO = filterDateFrom
      ? (() => { const d = new Date(filterDateFrom); d.setHours(0, 0, 0, 0); return d.toISOString(); })()
      : undefined;
    const dateToISO = filterDateTo
      ? (() => { const d = new Date(filterDateTo); d.setHours(23, 59, 59, 999); return d.toISOString(); })()
      : undefined;

    // Fetch soft-deleted items from all 4 tables in parallel
    const queries = [
      {
        table: "conversations" as const,
        resourceType: "chat" as ResourceType,
        nameField: "title",
      },
      {
        table: "dashboard_snapshots" as const,
        resourceType: "dashboard" as ResourceType,
        nameField: "title",
      },
      {
        table: "saved_queries" as const,
        resourceType: "query" as ResourceType,
        nameField: "name",
      },
      {
        table: "workflows" as const,
        resourceType: "workflow" as ResourceType,
        nameField: "name",
      },
    ];

    const results = await Promise.all(
      queries.map(async ({ table, resourceType, nameField }) => {
        let query = supabase
          .from(table as any)
          .select(`id, ${nameField}, deleted_at, deleted_by, created_at, client_id`)
          .eq("client_id", profile.client_id!)
          .not("deleted_at", "is", null);

        if (filterType !== "all" && filterType !== resourceType) {
          return []; // Skip this table if not matching the filter
        }

        if (dateFromISO) {
          query = query.gte("deleted_at", dateFromISO);
        }
        if (dateToISO) {
          query = query.lte("deleted_at", dateToISO);
        }

        query = query.order("deleted_at", { ascending: false });

        const { data, error } = await query;
        if (error || !data) return [];

        return (data as any[]).map((row) => ({
          id: row.id,
          name: row[nameField] || "Untitled",
          resource_type: resourceType,
          table_name: table,
          deleted_at: row.deleted_at,
          deleted_by_name: null,
          created_at: row.created_at,
        }));
      }),
    );

    const allItems: RecycleBinItem[] = results.flat();
    // Sort all items by deleted_at descending
    allItems.sort((a, b) => new Date(b.deleted_at).getTime() - new Date(a.deleted_at).getTime());

    setItems(allItems);
    setSelected(new Set());
    setLoading(false);
  }, [profile?.client_id, filterType, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleRestore = async (item: RecycleBinItem) => {
    setActionLoading(item.id);
    const { error } = await supabase
      .from(item.table_name as any)
      .update({ deleted_at: null, deleted_by: null })
      .eq("id", item.id);

    if (error) {
      toast({ title: "Error", description: "Failed to restore item", variant: "destructive" });
    } else {
      // If restoring a conversation, also restore its dashboards
      if (item.table_name === "conversations") {
        await supabase
          .from("dashboard_snapshots" as any)
          .update({ deleted_at: null, deleted_by: null })
          .eq("conversation_id", item.id);
      }
      auditLog({
        action: "restored",
        resourceType: item.resource_type,
        resourceId: item.id,
        resourceName: item.name,
      });
      toast({ title: "Restored", description: `"${item.name}" has been restored` });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setSelected((prev) => { const next = new Set(prev); next.delete(item.id); return next; });
    }
    setActionLoading(null);
  };

  const handlePermanentDelete = async (item: RecycleBinItem) => {
    setActionLoading(item.id);

    let error: any = null;

    if (item.table_name === "conversations") {
      // Delete messages and dashboards first
      await supabase.from("messages").delete().eq("conversation_id", item.id);
      await supabase.from("dashboard_snapshots").delete().eq("conversation_id", item.id);
      const res = await supabase.from("conversations").delete().eq("id", item.id);
      error = res.error;
    } else {
      const res = await supabase.from(item.table_name as any).delete().eq("id", item.id);
      error = res.error;
    }

    if (error) {
      toast({ title: "Error", description: "Failed to permanently delete item", variant: "destructive" });
    } else {
      auditLog({
        action: "permanent_deleted",
        resourceType: item.resource_type,
        resourceId: item.id,
        resourceName: item.name,
        metadata: { permanently_deleted: true },
      });
      toast({ title: "Permanently deleted", description: `"${item.name}" has been permanently deleted` });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setSelected((prev) => { const next = new Set(prev); next.delete(item.id); return next; });
    }
    setActionLoading(null);
    setSingleDeleteTarget(null);
  };

  const handleBulkRestore = async () => {
    const selectedItems = items.filter((i) => selected.has(i.id));
    for (const item of selectedItems) {
      await handleRestore(item);
    }
  };

  const handleBulkPermanentDelete = async () => {
    setActionLoading("bulk");
    const selectedItems = items.filter((i) => selected.has(i.id));
    for (const item of selectedItems) {
      let error: any = null;
      if (item.table_name === "conversations") {
        await supabase.from("messages").delete().eq("conversation_id", item.id);
        await supabase.from("dashboard_snapshots").delete().eq("conversation_id", item.id);
        const res = await supabase.from("conversations").delete().eq("id", item.id);
        error = res.error;
      } else {
        const res = await supabase.from(item.table_name as any).delete().eq("id", item.id);
        error = res.error;
      }
      if (!error) {
        auditLog({
          action: "permanent_deleted",
          resourceType: item.resource_type,
          resourceId: item.id,
          resourceName: item.name,
          metadata: { permanently_deleted: true, bulk_action: true },
        });
      }
    }
    toast({
      title: "Permanently deleted",
      description: `${selectedItems.length} item(s) permanently deleted`,
    });
    setBulkDeleteOpen(false);
    setActionLoading(null);
    fetchItems();
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  if (!profile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <Trash2 className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client assigned to your account.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      <div>
        <h1 className="text-2xl font-semibold">Recycle Bin</h1>
        <p className="text-sm text-muted-foreground">
          Restore or permanently delete items. Permanently deleted items cannot be recovered.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Type:</span>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="chat">Chat</SelectItem>
              <SelectItem value="dashboard">Dashboard</SelectItem>
              <SelectItem value="query">Query / Report</SelectItem>
              <SelectItem value="workflow">Workflow</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Deleted from:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[150px] justify-start text-left font-normal",
                  !filterDateFrom && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filterDateFrom ? format(filterDateFrom, "MMM d, yyyy") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterDateFrom}
                onSelect={setFilterDateFrom}
                disabled={(date) =>
                  date > new Date() || (filterDateTo ? date > filterDateTo : false)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">To:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[150px] justify-start text-left font-normal",
                  !filterDateTo && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filterDateTo ? format(filterDateTo, "MMM d, yyyy") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterDateTo}
                onSelect={setFilterDateTo}
                disabled={(date) =>
                  date > new Date() || (filterDateFrom ? date < filterDateFrom : false)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {(filterDateFrom || filterDateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterDateFrom(undefined);
              setFilterDateTo(undefined);
            }}
          >
            Clear dates
          </Button>
        )}
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/50 px-4 py-2">
          <span className="text-sm font-medium">{selected.size} item(s) selected</span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkRestore}
            disabled={actionLoading === "bulk"}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Restore selected
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setBulkDeleteOpen(true)}
            disabled={actionLoading === "bulk"}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete permanently
          </Button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Trash2 className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-1">Recycle bin is empty</p>
          <p className="text-sm text-muted-foreground">
            Deleted items will appear here for recovery or permanent removal.
          </p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={selected.size === items.length && items.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Deleted At</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`${item.table_name}-${item.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(item.id)}
                      onCheckedChange={() => toggleSelect(item.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium max-w-[250px] truncate">
                    {item.name}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={RESOURCE_COLORS[item.resource_type]}>
                      {RESOURCE_LABELS[item.resource_type]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {format(new Date(item.deleted_at), "MMM d, yyyy HH:mm")}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {format(new Date(item.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRestore(item)}
                        disabled={actionLoading === item.id}
                        title="Restore"
                      >
                        {actionLoading === item.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4" />
                        )}
                        <span className="ml-1 hidden sm:inline">Restore</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setSingleDeleteTarget(item)}
                        disabled={actionLoading === item.id}
                        title="Delete permanently"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="ml-1 hidden sm:inline">Delete</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Confirm single permanent delete */}
      <AlertDialog open={!!singleDeleteTarget} onOpenChange={(open) => !open && setSingleDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Permanently delete?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>"{singleDeleteTarget?.name}"</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => singleDeleteTarget && handlePermanentDelete(singleDeleteTarget)}
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm bulk permanent delete */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Permanently delete {selected.size} item(s)?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all selected items. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleBulkPermanentDelete}
            >
              {actionLoading === "bulk" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RecycleBin;
